import { useState, useCallback, useRef, useEffect } from 'react';
export type GameState = 'lobby' | 'howToPlay' | 'playing' | 'gameOver';
const STORAGE_KEY = 'block_dodge_best_score';
export function useGameState() {
  const [gameState, setGameState] = useState<GameState>('lobby');
  const [currentScore, setCurrentScore] = useState(0);
  const [bestScore, setBestScore] = useState(0);
  const scoreRef = useRef(0);
  const startedAtMsRef = useRef<number | null>(null);
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (!isNaN(parsed)) {
        setBestScore(parsed);
      }
    }
  }, []);
  const saveBestScore = useCallback((score: number) => {
    localStorage.setItem(STORAGE_KEY, score.toString());
    setBestScore(score);
  }, []);
  const startGame = useCallback(() => {
    setCurrentScore(0);
    scoreRef.current = 0;
    startedAtMsRef.current = Date.now();
    setGameState('playing');
  }, []);
  const incrementScore = useCallback((delta: number) => {
    scoreRef.current += delta;
    setCurrentScore(Math.floor(scoreRef.current));
  }, []);
  const endGame = useCallback(() => {
    const finalScore = Math.floor(scoreRef.current);
    if (finalScore > bestScore) {
      saveBestScore(finalScore);
    }
    setGameState('gameOver');
  }, [bestScore, saveBestScore]);
  const resetGame = useCallback(() => {
    setGameState('lobby');
    setCurrentScore(0);
    scoreRef.current = 0;
    startedAtMsRef.current = null;
  }, []);
  const setHowToPlay = useCallback((show: boolean) => {
    setGameState(show ? 'howToPlay' : 'lobby');
  }, []);
  const returnToLobby = useCallback(() => {
    setGameState('lobby');
  }, []);
  return {
    gameState,
    currentScore,
    bestScore,
    scoreRef,
    startedAtMsRef,
    startGame,
    incrementScore,
    endGame,
    resetGame,
    setHowToPlay,
    returnToLobby,
  };
}